/**
 * Which rows the bulk bar is about to act on.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Two rules keep a bulk action honest, and both are about rows the user can no
 * longer see:
 *
 * 1. Switching segment or layer CLEARS the selection. Ticking three eliminated
 *    rows and then hitting "Move to live now" on the Queue tab would otherwise
 *    act on rows that scrolled out of existence.
 * 2. A row that disappears from the listing drops out of the selection. The
 *    queue refetches after every rotation, so an element another tab (or the
 *    rotation itself) has just moved must not stay armed.
 *
 * Keyed by `layer:bankId`, so the SAME bank element live under several pools is
 * one selection and one call — the demote/promote endpoints take a bank id, not
 * a pool row, and sending it twice would be one write and one confusing 409.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { QueueElement } from '@trafficpulse/shared';

export const selectionKey = (el: QueueElement) => `${el.layer}:${el.bankId}`;

export interface QueueSelection {
  ids: ReadonlySet<string>;
  /** Selected rows, de-duplicated, in the order the table draws them. */
  selected: QueueElement[];
  count: number;
  allSelected: boolean;
  isSelected: (el: QueueElement) => boolean;
  toggle: (el: QueueElement) => void;
  toggleAll: () => void;
  clear: () => void;
}

export function useQueueSelection(
  rows: QueueElement[],
  /** Changing this clears the selection — pass segment + layer. */
  resetKey: string,
): QueueSelection {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set<string>());

  useEffect(() => {
    setIds((prev) => (prev.size === 0 ? prev : new Set<string>()));
  }, [resetKey]);

  // One entry per element, first occurrence wins, on-screen order preserved.
  const visible = useMemo(() => {
    const map = new Map<string, QueueElement>();
    for (const row of rows) {
      const key = selectionKey(row);
      if (!map.has(key)) map.set(key, row);
    }
    return map;
  }, [rows]);

  useEffect(() => {
    setIds((prev) => {
      const kept = [...prev].filter((id) => visible.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [visible]);

  const selected = useMemo(
    () => [...visible.values()].filter((el) => ids.has(selectionKey(el))),
    [visible, ids],
  );

  const toggle = useCallback((el: QueueElement) => {
    const key = selectionKey(el);
    setIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setIds((prev) =>
      prev.size === visible.size ? new Set<string>() : new Set(visible.keys()),
    );
  }, [visible]);

  const clear = useCallback(() => setIds(new Set<string>()), []);

  return {
    ids,
    selected,
    count: selected.length,
    allSelected: visible.size > 0 && selected.length === visible.size,
    isSelected: (el) => ids.has(selectionKey(el)),
    toggle,
    toggleAll,
    clear,
  };
}
